<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { differenceInHours, formatDistanceToNow } from 'date-fns';
import { OnClickOutside } from '@vueuse/components';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';
import { useDateFnsLocale } from 'shared/composables/useDateFnsLocale';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import KanbanConditionPill from './KanbanConditionPill.vue';
import { usePopoverTrigger } from '../composables/usePopoverTrigger';
import { contactCardIdentity } from '../helpers/kanbanContact';
import {
  DEBT_STATE_PILL_CONFIG,
  SLA_BREACHED_PILL,
} from '../helpers/kanbanConditions';

const props = defineProps({
  card: { type: Object, required: true },
  // Etapa correspondente à coluna que renderiza este card — não a etapa do
  // card em si (são sempre a mesma coisa em uso normal, mas o card não traz
  // sla_hours embutido: _card.json.jbuilder só expõe stage_id, o objeto de
  // etapa completo vem de funnels()). Ver useKanbanBoard#stageById.
  stage: { type: Object, default: null },
});

const { t } = useI18n();
const route = useRoute();
// date-fns 2.21.1 não tem locale global (setDefaultOptions só existe da 3.x):
// o locale vai em CADA chamada que renderiza palavra. Sem ele o rodapé do
// card sai "há 29 days nesta etapa" — moldura traduzida com o miolo em
// inglês, que se lê pior que a frase inteira em inglês.
const { dateFnsLocale } = useDateFnsLocale();
// Mesmo tratamento que a lista de conversas do produto dá ao mesmo dado
// (CardMessagePreview.vue): o conteúdo cru traz marcação (markdown do
// WhatsApp, corpo de e-mail) que apareceria literal na prévia.
const { getPlainText } = useMessageFormatter();

const conversation = computed(() => props.card.conversation);
const contact = computed(() => conversation.value.contact);
const assignee = computed(() => conversation.value.assignee);

// Contato de WhatsApp sem nome salvo tem name == phone_number, e o card
// gastava duas linhas com o mesmo número. contactCardIdentity decide qual é
// o título e se o telefone é um dado à parte — ver o helper para o caso
// `Angela 5547999660243`, que NÃO pode colapsar.
const identity = computed(() => contactCardIdentity(contact.value));

// O telefone perdeu a LINHA PRÓPRIA que tinha em todo card: ele quase nunca
// decide a triagem e ocupava a segunda posição de peso visual, à frente da
// condição. Quando o contato não tem nome salvo ele continua sendo o título
// (contactCardIdentity), que é o caso da maioria dos contatos de WhatsApp;
// quando há nome de verdade, ele sobrevive no tooltip do próprio controle —
// o mesmo atributo que já cobria o truncamento do nome.
const identityTitle = computed(() =>
  identity.value.phone
    ? t('KANBAN.BOARD.CARD.IDENTITY_TITLE', {
        name: identity.value.title,
        phone: identity.value.phone,
      })
    : identity.value.title
);

// A lista de conversas do produto lidera com nome + última mensagem; o card
// do board ignorava a prévia que a API já entrega (_card.json.jbuilder expõe
// last_message_preview, com `[Anexo]` quando a mensagem é só anexo). Sem
// prévia não há linha vazia: o card tem outras linhas e não colapsa.
const messagePreview = computed(() => {
  const preview = conversation.value.last_message_preview;
  return preview ? getPlainText(preview) : '';
});

// O board FILTRA por etiqueta e não mostrava etiqueta nenhuma. A cor vem da
// lista de etiquetas da conta, fornecida uma vez pela página
// (KanbanBoardIndex.vue) e injetada aqui — resolver por card faria N leituras
// do store para o mesmo dado, e KanbanColumn está entre a página e o card.
// Etiqueta ainda sem cor conhecida (a lista da conta carrega de forma
// assíncrona, e o board renderiza antes dela) aparece com o ponto neutro em
// vez de sumir: o título é o dado, a cor é o reforço.
const accountLabels = inject('kanbanAccountLabels', null);

const cardLabels = computed(() => {
  const titles = conversation.value.labels ?? [];
  if (!titles.length) return [];

  const colorByTitle = new Map(
    (accountLabels?.value ?? []).map(label => [label.title, label.color])
  );
  return titles.map(title => ({ title, color: colorByTitle.get(title) }));
});

// unread_incoming_messages trava em `.last(10)` no servidor
// (_card.json.jbuilder), então 10 aqui significa "10 ou mais". A contagem
// crua que a lista de conversas mostra afirmaria um total exato que esta API
// não tem — o corte em "9+" é o que o dado suporta (ver o badge abaixo para
// o desenho).
const unreadLabel = computed(() =>
  conversation.value.unread_count > 9
    ? t('CHAT_LIST.UNREAD_COUNT_OVERFLOW')
    : conversation.value.unread_count
);

const enteredStageDate = computed(() =>
  props.card.entered_stage_at ? new Date(props.card.entered_stage_at) : null
);

const isSlaBreached = computed(() => {
  if (!props.stage?.sla_hours || !enteredStageDate.value) return false;
  return (
    differenceInHours(new Date(), enteredStageDate.value) >=
    props.stage.sla_hours
  );
});

const timeInStageLabel = computed(() => {
  if (!enteredStageDate.value) return '';
  const time = formatDistanceToNow(enteredStageDate.value, {
    locale: dateFnsLocale.value,
  });
  if (isSlaBreached.value) {
    return t('KANBAN.BOARD.CARD.TIME_IN_STAGE_SLA_BREACHED', {
      time,
      slaHours: props.stage.sla_hours,
    });
  }
  return t('KANBAN.BOARD.CARD.TIME_IN_STAGE', { time });
});

const debtStatePill = computed(() => {
  const state = conversation.value.debt_state;
  const config = state ? DEBT_STATE_PILL_CONFIG[state] : null;
  if (!config) return null;
  return {
    ...config,
    label: t(`CONVERSATION.DEBT_STATE_WIDGET.STATES.${state}`),
  };
});

const slaPill = computed(() => {
  if (!isSlaBreached.value) return null;
  return { ...SLA_BREACHED_PILL, label: t('KANBAN.BOARD.SLA_BREACHED') };
});

const conversationPath = computed(() =>
  frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: conversation.value.id,
    })
  )
);

// Abrir a conversa NÃO navega mais: sair do board descarta filtros, posição
// de rolagem e o contexto do funil inteiro para responder "o que essa pessoa
// disse?". O diálogo é montado pela página (KanbanBoardIndex.vue) e chega
// aqui por inject, como as demais ações que atravessam KanbanColumn.
const openConversation = inject('kanbanOpenConversation', null);
const nameTriggerRef = ref(null);

const onCardClick = event => {
  if (event.metaKey || event.ctrlKey) {
    window.open(
      window.chatwootConfig.hostURL + conversationPath.value,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  // Focar explicitamente o gatilho ANTES de abrir. O diálogo devolve o foco,
  // ao fechar, para o document.activeElement de quando ele abriu (2.4.3), e
  // clique de mouse em <button> não foca em todo navegador — no Safari não
  // foca. Sem esta linha, quem abre com o mouse recebe o foco de volta em
  // BODY, que é exatamente o defeito que a devolução existe para evitar.
  nameTriggerRef.value?.focus();
  openConversation?.(props.card);
};

// BLOQUEADOR 1 (revisor-frontend, T3.4, 2.1.1): SortableJS não opera por
// teclado — este é o caminho alternativo. KanbanColumn.vue fica entre este
// card e o estado do board (columnsByStageId em useKanbanBoard) e está sob
// edição concorrente de outro agente nesta mesma janela, então stages e a
// ação de mover chegam por provide/inject (fornecidos em KanbanBoardIndex.vue)
// em vez de props atravessando esse intermediário.
const kanbanStages = inject('kanbanStages', null);
const moveCardToStage = inject('kanbanMoveCardToStage', null);

const stageOptions = computed(() =>
  (kanbanStages?.value || []).map(s => ({ id: s.id, name: s.name }))
);

// Fechar devolvendo o foco ao gatilho é o padrão compartilhado dos popovers
// deste board (usePopoverTrigger): sem ele, Escape ou escolher uma etapa
// deixa o foco em BODY, porque o botão da opção escolhida sai do DOM junto
// com o painel.
const {
  isOpen: isMoveOpen,
  triggerRef: moveTriggerRef,
  toggle: toggleMove,
  close: closeMove,
  focusTrigger: focusMoveTrigger,
  closeAndFocusTrigger: closeMoveAndFocusTrigger,
} = usePopoverTrigger();
// Mover de verdade destrói ESTA instância (cada coluna tem seu próprio
// Draggable), então focar o gatilho aqui foca um botão que sai do DOM no
// flush seguinte — medido: o foco terminava em BODY. closeMoveAndFocusTrigger
// continua correto para o caso em que NADA se move (escolher a etapa atual:
// moveCardToStage retorna cedo e o card permanece montado); o caso do
// movimento real é cumprido pela instância nova, abaixo.
const onSelectStage = option => {
  moveCardToStage?.(props.card, option.id);
  closeMoveAndFocusTrigger();
};

// O pedido de foco NÃO é consumido aqui. A instância que monta no destino
// não sabe se o movimento vai ser aceito: quando o PATCH falha, o composable
// reverte, ESTA instância é desmontada e a instância remontada na origem
// precisa do mesmo pedido — zerá-lo aqui deixava o foco em BODY em toda
// falha de rede (achado A3 do refutador). Quem abre o pedido é quem o fecha:
// KanbanBoardIndex.vue o zera depois que o movimento se resolve, para os
// dois desfechos.
const focusCardIdAfterMove = inject('kanbanFocusCardIdAfterMove', null);
onMounted(() => {
  if (focusCardIdAfterMove?.value !== props.card.id) return;
  focusMoveTrigger();
});
</script>

<template>
  <div
    role="listitem"
    class="flex flex-col w-full gap-2 p-3 rounded-lg border border-n-weak bg-n-solid-1 hover:border-n-strong"
  >
    <div class="flex items-start justify-between gap-2">
      <!--
        h3, nao h4: a coluna e h2 (KanbanColumn.vue), entao h4 aqui pula um
        nivel e `heading-order` acusa 4 nos. E boa pratica, nao WCAG, mas o
        conserto e o proprio numero.
      -->
      <h3 class="m-0 min-w-0 flex-1 text-sm font-semibold">
        <!--
          CRITICO (revisor-frontend, WCAG 2.0 nivel A, 4.1.2 nested-interactive):
          o card inteiro era role=button com o "Move to stage" (um <button>
          real) dentro dele — nome acessível do card engolia o do botão
          interno ("... Move to s…") e a árvore virava um controle dentro de
          outro. A saída: o container deixa de ser interativo (div comum,
          role=listitem — ver KanbanColumn.vue para o role=list da coluna) e
          o NOME do contato passa a ser o elemento navegável, irmão do
          controle de mover, nunca ancestral dele. O span sr-only da etapa
          entra AQUI DENTRO (não mais como primeiro filho do card inteiro),
          para que o nome acessível deste botão carregue a etapa, do mesmo
          jeito que antes — só que agora sem arrastar telefone, pastilhas e
          responsável junto.
        -->
        <!--
          `relative` nao e decorativo. O `.sr-only` do Tailwind e
          `position: absolute` SEM `left`/`top`; sem um ancestral posicionado, o
          bloco contentor dele vira o <html>. Dentro do board, que rola
          horizontalmente, o span da 5a coluna aterrissava a ~1200px e esticava
          `documentElement.scrollWidth`: medido 845px de estouro do BODY em
          360px de viewport, 1130 a 200% de zoom (1.4.10 Reflow, AA).
          Provado por bisseccao ao vivo: `position: relative` aqui leva o
          estouro de 845 para 0.
        -->
        <button
          ref="nameTriggerRef"
          type="button"
          class="relative w-full p-0 text-left truncate text-n-slate-12"
          :title="identityTitle"
          aria-haspopup="dialog"
          @click="onCardClick"
        >
          <span v-if="stage" class="sr-only">
            {{ t('KANBAN.BOARD.CARD.STAGE_CONTEXT', { stage: stage.name }) }}
          </span>
          {{ identity.title }}
        </button>
      </h3>
      <!--
        O mesmo dado tem duas marcações distintas no produto, e nenhuma serve
        de referência sem medir. A lista de conversas (CardContent.vue ->
        UnreadBadge.vue) usa teal-9/16px/10px; existe também
        CardMessagePreview.vue com bg-n-brand/20px/12px, mas ele NÃO é o que a
        lista renderiza — é uma segunda implementação do mesmo dado, sem uso
        ativo nesta versão do produto.
        Medido na tela real: #FFFFFF sobre #2781F6 (bg-n-brand), 12px/600, dá
        3,78:1 nos dois temas (bg-n-brand não muda entre claro e escuro) —
        abaixo do mínimo de 4,5:1 do WCAG 1.4.3 (12px não entra na exceção de
        texto grande, que exige >=18,66px em negrito). Copiar o teal-9 da
        lista não resolveria: 3,07:1, também abaixo do mínimo. bg-n-iris-9
        mantém o mesmo tratamento (círculo sólido, texto branco) e passa:
        5,37:1 nos dois temas (iris-9 também não muda entre claro e escuro;
        mesmo par já usado em CopilotReplyBottomPanel.vue e
        CopilotEditor.vue). A marcação continua inline, e não o UnreadBadge
        compartilhado, porque a diferença não é só de cor: a contagem desta
        API é LIMITADA em 10 (ver unreadLabel), e mudar o componente
        compartilhado levaria o corte "9+" para as telas que têm a contagem
        exata.
      -->
      <span
        v-if="conversation.unread_count > 0"
        data-testid="kanban-card-unread"
        class="inline-flex items-center justify-center rounded-full shrink-0 size-5 bg-n-iris-9"
      >
        <span class="text-xs font-semibold text-white">{{ unreadLabel }}</span>
      </span>
    </div>

    <!--
      A CONDIÇÃO SOBE. Ela era o menor texto do card (10px) e vinha abaixo do
      telefone; é ela que decide a triagem ("SLA estourado", "Estagnado"),
      então passa a 12px e ocupa a linha logo abaixo do nome — acima da
      prévia, que informa, e do rodapé, que contextualiza.
    -->
    <div
      v-if="debtStatePill || slaPill"
      class="flex flex-wrap items-center gap-1"
    >
      <KanbanConditionPill
        v-if="debtStatePill"
        :icon="debtStatePill.icon"
        :label="debtStatePill.label"
        :classes="debtStatePill.classes"
      />
      <KanbanConditionPill
        v-if="slaPill"
        :icon="slaPill.icon"
        :label="slaPill.label"
        :classes="slaPill.classes"
      />
    </div>

    <!--
      Prévia da última mensagem: o elemento com que a lista de conversas do
      produto lidera, e que este card não tinha. Duas linhas (line-clamp-2,
      como CardMessagePreview.vue) porque uma só corta a frase no meio em
      português; o :title devolve o texto inteiro no truncamento.
    -->
    <!--
      A prévia é CONTEÚDO, não rodapé — e a distinção é medida, não estética.
      Ela entrou com `text-xs`/`slate-11`, exatamente o mesmo token e tamanho
      da etiqueta, do responsável e do tempo-na-etapa: quatro linhas cinzas
      tipograficamente indistinguíveis, com a fala do cliente pesando igual ao
      carimbo de relógio. A página-irmã do produto (a lista de conversas)
      trata a prévia como conteúdo: `slate-12`, o MESMO tom do nome.
      `text-sm` + `slate-12` alinha o board ao idioma do produto e separa "do
      que a conversa trata" de "quando foi" — que é a leitura que decide
      triagem. slate-12 é o token de texto primário, então a mudança sobe o
      contraste (5,94:1 medido em slate-11) em vez de baixá-lo.
    -->
    <p
      v-if="messagePreview"
      data-testid="kanban-card-preview"
      class="m-0 text-sm leading-snug text-n-slate-12 line-clamp-2"
      :title="messagePreview"
    >
      {{ messagePreview }}
    </p>

    <!--
      Etiquetas: o board filtra por etiqueta desde T3.4 e não mostrava
      nenhuma. Ponto colorido + título é o tratamento da lista de conversas
      (CardLabels.vue), inclusive o `style` da cor — a cor da etiqueta é um
      hex por conta, que nenhuma classe utilitária pode expressar.
    -->
    <ul
      v-if="cardLabels.length"
      data-testid="kanban-card-labels"
      class="flex flex-wrap items-center gap-x-2 gap-y-1 m-0 p-0 list-none"
    >
      <li
        v-for="label in cardLabels"
        :key="label.title"
        class="flex items-center gap-1 min-w-0"
      >
        <span
          aria-hidden="true"
          class="rounded-full size-1.5 shrink-0 bg-n-slate-9"
          :style="{ backgroundColor: label.color }"
        />
        <span class="text-xs truncate text-n-slate-11" :title="label.title">
          {{ label.title }}
        </span>
      </li>
    </ul>

    <!--
      ALTO (revisor-frontend, T3.4): rodapé em duas linhas — o tempo-na-etapa
      ganha linha própria em vez de disputar largura com o nome do
      responsável (o rótulo com SLA ocupava 198,3 dos 264px úteis e não
      cedia, espremendo o nome a 5,7px em 2 dos 8 cards, exatamente onde o
      SLA já estourou). A segunda linha resolve por quebra de linha, uma das
      opções aceitas na delegação.
    -->
    <div class="flex flex-col gap-1">
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-1.5 min-w-0 flex-1">
          <!--
            BLOQUEADOR 3/4 (revisor-frontend, T3.4): Avatar (componente
            compartilhado, fora de escopo — ver RISCOS do retorno desta
            unidade) renderiza iniciais a 3,82:1 no claro (par
            #CCF3EA/#008573 do palette interno, o pior dos 6; medido, não a
            paleta inteira falha) e o :size=20 anterior descia a fonte a 8px
            (Math.min(size/2.5,24) em Avatar.vue), agravando a leitura de um
            texto já no limite. size=32 (12,8px) melhora a legibilidade mas
            NÃO muda a razão de contraste em si — cor é invariante ao
            tamanho — nem cruza o corte de "texto grande" do WCAG 1.4.3
            (>=24px sem negrito; a classe font-medium interna do Avatar não
            conta como negrito), que exigiria size>=60 e desproporcionaria o
            rodapé do card. Conformidade plena depende da paleta do próprio
            Avatar.
          -->
          <Avatar
            v-if="assignee"
            :name="assignee.name"
            :src="assignee.thumbnail"
            :size="32"
            hide-offline-status
            rounded-full
          />
          <!--
            :title medido, não presumido: "Ana Paula Nogueira Vasconcelos"
            ocupa 183px dos 128px úteis no piso de 240px da coluna e truncava
            SEM nenhuma forma de recuperar o nome inteiro (o rótulo curto
            "Sem responsável", 95px, não trunca).
          -->
          <span
            class="text-xs truncate text-n-slate-11"
            :title="
              assignee ? assignee.name : t('KANBAN.BOARD.CARD.UNASSIGNED')
            "
          >
            {{ assignee ? assignee.name : t('KANBAN.BOARD.CARD.UNASSIGNED') }}
          </span>
        </div>

        <!--
          BLOQUEADOR 1: caminho por teclado para mover o card entre etapas,
          reusando MultiselectDropdownItems (mesma peça que T3.3 já usa via
          MultiselectDropdown, e que KanbanMultiSelectFilter.vue já usa
          diretamente neste board). O nome acessível é o PROPÓSITO
          ("Move to stage"), não o valor — por isso aria-label fixo em vez de
          deixar o texto visível da etapa atual virar o nome acessível do
          botão (NextButton aceita aria-label como atributo comum: cai direto
          no <button> real via filteredAttrs, sem precisar editar o
          componente). click/keydown param aqui (.stop) é defensivo: desde a
          correção do 4.1.2 o card não tem mais handler de clique no
          container (só o nome, irmão deste bloco, é clicável), então não há
          mais um ancestral para o clique borbulhar hoje — mantido para que
          um ancestral clicável adicionado no futuro não reabra o problema.
        -->
        <OnClickOutside @trigger="closeMove">
          <div class="relative shrink-0" @click.stop @keydown.stop>
            <NextButton
              ref="moveTriggerRef"
              slate
              ghost
              xs
              icon="i-lucide-arrow-right-left"
              :aria-label="t('KANBAN.BOARD.CARD.MOVE_TO_STAGE')"
              aria-haspopup="true"
              :aria-expanded="isMoveOpen"
              @click="toggleMove"
            />
            <div
              v-if="isMoveOpen"
              data-testid="kanban-card-move-options"
              class="absolute z-30 right-0 mt-1 w-56 rounded-lg border border-n-weak bg-n-solid-2 shadow-lg p-2"
              @keyup.esc="closeMoveAndFocusTrigger"
            >
              <MultiselectDropdownItems
                :options="stageOptions"
                :selected-items="[stage]"
                :has-thumbnail="false"
                :input-placeholder="
                  t('KANBAN.BOARD.MULTISELECT.SEARCH_PLACEHOLDER')
                "
                :no-search-result="t('KANBAN.BOARD.MULTISELECT.NO_RESULTS')"
                @select="onSelectStage"
              />
            </div>
          </div>
        </OnClickOutside>
      </div>

      <!--
        Hierarquia: o tempo-na-etapa repete em TODO card e no caso normal é
        rodapé, não dado crítico. A separação é feita subindo o peso do caso
        que importa (SLA estourado ganha font-medium, além da cor âmbar que
        já tinha) e não baixando o do caso normal: reduzir tamanho ou clarear
        a cor do texto rotineiro mexeria em contraste, que aqui é portão
        binário já medido em 4,5:1 com text-n-slate-11.
      -->
      <!--
        :title medido: em português o rótulo com SLA ("há 8 dias nesta etapa,
        acima da meta de 48h") ocupa 254px dos 198px úteis e perde 56px —
        corta exatamente no VALOR da meta ("...acima da "). O fato do estouro
        sobrevive na pastilha; o número da meta era irrecuperável. Sem SLA o
        rótulo cabe (198/198) e o atributo é inócuo.
      -->
      <span
        v-if="timeInStageLabel"
        class="text-xs truncate"
        :class="
          isSlaBreached ? 'font-medium text-n-amber-12' : 'text-n-slate-11'
        "
        :title="timeInStageLabel"
      >
        {{ timeInStageLabel }}
      </span>
    </div>
  </div>
</template>
